import React from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Globe, ExternalLink, RefreshCw, Shield } from 'lucide-react';

interface Verification {
  domain: string;
  verified_at: string | null;
  ssl_status: string;
}

interface DomainStep4CompleteProps {
  verification: Verification;
  onStartOver: () => void;
}

export function DomainStep4Complete({ verification, onStartOver }: DomainStep4CompleteProps) {
  return (
    <div className="space-y-6">
      <div className="text-center pb-4">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
          <CheckCircle2 className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-semibold mb-2">Domain Verified!</h2>
        <p className="text-muted-foreground">
          Your custom domain has been successfully verified
        </p>
      </div>

      <Card className="overflow-hidden border-green-200 dark:border-green-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="h-8 w-8 text-green-600" />
              <div>
                <p className="font-medium text-lg">{verification.domain}</p>
                <p className="text-sm text-muted-foreground">
                  Verified on {verification.verified_at 
                    ? new Date(verification.verified_at).toLocaleDateString() 
                    : 'N/A'}
                </p>
              </div>
            </div>
            <Badge className="bg-green-600">Active</Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="p-4 flex items-start gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">SSL Certificate</p>
              <p className="text-sm text-muted-foreground">
                {verification.ssl_status === 'active' ? (
                  <span className="text-green-600">Active and secured</span>
                ) : (
                  <span className="text-amber-600">Provisioning in progress...</span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-start gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <ExternalLink className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">Access Your Dashboard</p>
              <a 
                href={`https://${verification.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                https://{verification.domain}
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      <Alert className="bg-muted/50">
        <AlertDescription className="text-sm">
          Your white-labeled dashboard is now accessible at your custom domain.
          SSL certificate provisioning may take a few minutes to complete.
        </AlertDescription>
      </Alert>

      <div className="flex justify-center pt-4">
        <Button variant="outline" onClick={onStartOver} className="min-h-[44px]">
          <RefreshCw className="mr-2 h-4 w-4" />
          Add Another Domain
        </Button>
      </div>
    </div>
  );
}
